import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Global axios interceptor for auth
const tokenRef = { current: null };

axios.interceptors.request.use((config) => {
  if (tokenRef.current) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${tokenRef.current}`;
  }
  config.withCredentials = true;
  return config;
});

const AuthContext = createContext(null);

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const saveToken = (token) => {
    tokenRef.current = token;
    if (token) {
      localStorage.setItem("vizmo_token", token);
    } else {
      localStorage.removeItem("vizmo_token");
    }
  };

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Restore token from localStorage
    const stored = localStorage.getItem("vizmo_token");
    if (stored) {
      tokenRef.current = stored;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
    } catch {
      setUser(null);
      saveToken(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      if (data.mfa_required) {
        return { success: false, mfa_required: true };
      }
      saveToken(data.access_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const verifyMfa = async (code) => {
    try {
      const { data } = await axios.post(`${API}/auth/mfa/verify`, { code });
      saveToken(data.access_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name, marketingConsent = false) => {
    try {
      const { data } = await axios.post(`${API}/auth/register`, { email, password, name, marketing_consent: marketingConsent, terms_accepted: true });
      saveToken(data.access_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const loginWithGoogle = () => {
    alert("Google login coming soon. Please use email and password.");
  };

  const exchangeGoogleSession = async (sessionId) => {
    try {
      const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
      saveToken(data.access_token);
      setUser(data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch {}
    saveToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(data);
    } catch {
      // silently fail
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, loginWithGoogle, exchangeGoogleSession, setUser, verifyMfa, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
